import type { Subscription, Team, TeamMember, User } from '@unriddle/db/types';
import dayjs from 'dayjs';

export function toTitleCase(str: string) {
  return str.replace(
    /\w\S*/g,
    (txt) => txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase(),
  );
}

export type UserWithSubscription = Pick<User, 'id'> & {
  subscription: Subscription[];
  teamsMemberOf: (TeamMember & {
    team: Team & {
      subscription: Subscription | null;
    };
  })[];
};

const PREMIUM_FILE_SIZE_LIMIT = 300;

export function getUserPlanDetails(user: UserWithSubscription) {
  let planName = 'FREE';
  let fileSizeLimit: number;
  let pageLimit: number;
  let billingInterval = 'NONE';
  let status = 'CANCELED';

  let createdAt: string | null = null;
  let isFriendPlan = false; // Initialize the boolean flag

  const activeSubscription = user.subscription?.find(
    (sub: Subscription) => sub.status === 'ACTIVE',
  );

  const teamsMemberOf = user.teamsMemberOf.find(
    (
      member: TeamMember & {
        team: Team & {
          subscription: Subscription | null;
        };
      },
    ) => member.status === 'ACTIVE',
  );
  const teamSubscription = teamsMemberOf?.team.subscription;

  if (teamSubscription?.status === 'ACTIVE') {
    planName = teamSubscription.plan;
    billingInterval = teamSubscription.billingInterval;
    status = teamSubscription.status;
    createdAt = teamSubscription.createdAt;
  } else if (activeSubscription) {
    // Check if the user has an active subscription
    planName = activeSubscription.plan;
    billingInterval = activeSubscription.billingInterval;
    status = activeSubscription.status;
    createdAt = activeSubscription.createdAt;
  }

  // Set limits based on the plan name
  switch (planName) {
    case 'FREE':
      fileSizeLimit = 20 * 1024 * 1024;
      pageLimit = 120;
      break;
    case 'PRO':
      fileSizeLimit = PREMIUM_FILE_SIZE_LIMIT * 1024 * 1024;
      pageLimit = 10000;
      break;
    case 'PREMIUM':
      fileSizeLimit = PREMIUM_FILE_SIZE_LIMIT * 1024 * 1024;
      pageLimit = 10000;
      break;
    case 'FRIEND':
      planName = 'PREMIUM'; // Treat FRIEND as PREMIUM for limits
      fileSizeLimit = PREMIUM_FILE_SIZE_LIMIT * 1024 * 1024;
      pageLimit = 10000;
      isFriendPlan = true; // Set the flag to true for FRIEND plan
      break;
    case 'TEAM':
      fileSizeLimit = PREMIUM_FILE_SIZE_LIMIT * 1024 * 1024;
      pageLimit = 10000;
      break;
    default:
      fileSizeLimit = 30 * 1024 * 1024;
      pageLimit = 120;
      planName = 'FREE';
  }

  const formattedCreatedAt = dayjs(createdAt).format('YYYY-MM-DD HH:mm:ss');

  // Convert planName to sentence case
  planName = toTitleCase(planName.toLowerCase());

  return {
    planName,
    fileSizeLimit,
    pageLimit,
    billingInterval,
    status,
    createdAt: formattedCreatedAt,
    isFriendPlan,
  };
}
